import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CloudUpload, Eye, Download, Loader2 } from 'lucide-react';
import './qc-approve-modal.css';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtKb = (b) => (b || 0) < 1024 * 1024 ? `${Math.max(1, Math.round((b || 0) / 1024))} KB` : `${((b || 0) / (1024 * 1024)).toFixed(1)} MB`;

const PdfGlyph = () => (
  <div className="relative w-10 h-11 rounded-lg bg-red-50 border border-red-100 grid place-items-center shrink-0" aria-hidden>
    <span className="text-[9px] font-black tracking-wide text-white bg-red-600 rounded px-1 py-0.5">PDF</span>
  </div>
);

const Pill = ({ children }) => (
  <span className="text-[11px] font-semibold text-violet-700 bg-violet-100/80 border border-violet-200 rounded-full px-2.5 py-0.5">{children}</span>
);

/* QC-PORTAL-HANDOFF-001 — External Portal: QC final-package documents the completing user must upload to the client portal. */
export const PortalHandoffSection = ({ orderId, documents = [], checked, onChange, disabled = false, dense = false }) => {
  const [viewing, setViewing] = useState(null);
  if (!documents.length) return null;
  const token = localStorage.getItem('token');
  const many = documents.length > 1;

  const view = async (d) => {
    setViewing(d.file_id);
    try {
      const r = await axios.get(`${API}/api/orders/${orderId}/files/${d.file_id}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error(`Could not open ${d.filename}`); }
    finally { setViewing(null); }
  };

  const btn = `inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 font-semibold text-sky-800 hover:bg-sky-100 transition-colors ${dense ? 'h-8 px-3 text-[12px]' : 'h-9 px-3.5 text-[13px]'}`;

  return (
    <section className="rounded-xl border border-violet-200 bg-violet-50/50 overflow-hidden" data-testid="portal-handoff-section">
      <header className="flex items-center gap-3 px-4 py-3">
        <span className="grid place-items-center w-9 h-9 rounded-full bg-violet-100 text-violet-700"><CloudUpload className="h-[18px] w-[18px]" /></span>
        <h3 className="flex-1 text-[15px] font-semibold text-slate-900">Documents to Client Portal ({documents.length})</h3>
        <Pill>Required</Pill>
      </header>
      <ul className="px-4 pb-3 space-y-2">
        {documents.map((d) => (
          <li key={d.file_id} className={`qcam-doc flex items-center gap-3 rounded-xl bg-white border border-violet-100 px-3 py-2.5 ${dense ? 'flex-wrap' : ''}`} data-testid={`portal-handoff-doc-${d.file_id}`}>
            <PdfGlyph />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold text-slate-900" title={d.filename}>{d.filename}</p>
              <p className="truncate text-[12px] text-slate-500">{fmtKb(d.file_size)} <span className="mx-1">•</span> Added by {d.uploaded_by || 'QC'} <span className="mx-1">•</span> Final Package</p>
            </div>
            <div className={`flex items-center gap-2 ${dense ? 'basis-full justify-end' : ''}`}>
              <button type="button" onClick={() => view(d)} disabled={viewing === d.file_id} className={btn} data-testid={`portal-handoff-view-${d.file_id}`}>
                {viewing === d.file_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} View
              </button>
              <a href={`${API}/api/orders/${orderId}/files/${d.file_id}/direct-download?token=${encodeURIComponent(token)}`} download={d.filename} className={btn} data-testid={`portal-handoff-download-${d.file_id}`}>
                <Download className="h-4 w-4" /> Download
              </a>
            </div>
          </li>
        ))}
      </ul>
      <label className={`qcam-ack flex items-center gap-3 px-4 py-3 border-t border-violet-100 bg-violet-50/70 select-none ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
        <input type="checkbox" className="sr-only" checked={!!checked} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} data-testid="portal-handoff-ack-checkbox" />
        <span className="qcam-check" data-checked={!!checked}><svg viewBox="0 0 24 24"><polyline points="5 12.5 10 17.5 19 7.5" /></svg></span>
        <span className="text-[14px] font-medium text-slate-800">
          I confirm {many ? `these ${documents.length} documents have` : 'this document has'} been uploaded to the client portal.
        </span>
      </label>
    </section>
  );
};

export default PortalHandoffSection;
